// Single river card / hero card / row variants.
//
// In the unified-lens world, each card shows BOTH applicable lens icons
// (🚣 + 🎣 for dual-cat rivers) and shows a status pill PER applicable
// lens. So a dual-cat river that's "Runnable for whitewater + Ideal for
// fishing" gets two clearly-labeled status badges side by side.
//
// The `primaryCat` arg is the lens to use for the accent color (left edge
// of the card). For dual-cat rivers this is typically whichever lens
// scores best for today.

import type { RiverAnalysis, RiverCategory } from '../../functions/_lib/types';
import {
  formatCfs,
  formatPercent,
  percentileBadgeClass,
  speciesShortList,
  statusBadgeClass,
  statusFor,
  statusLabel,
  trendIcon,
} from '../lib/format';

export function renderRiverCard(r: RiverAnalysis, primaryCat: RiverCategory): string {
  const primaryStatus = statusFor(r, primaryCat);
  const accentBg = accentColor(primaryStatus, primaryCat);
  const sparkId = `spark-${r.id}`;

  // Narrative excerpt — show the narrative for the primary lens; if it's
  // empty, fall back to the other lens's narrative if available.
  const narrative = bestNarrative(r, primaryCat);
  const excerpt = narrative
    ? truncate(narrative.replace(/\s+/g, ' ').replace(/\*\*/g, '').trim(), 160)
    : null;

  return `
    <a href="#/r/${r.id}"
       class="river-card group block relative rounded-2xl bg-base-200/60 backdrop-blur border border-base-content/5 hover:border-primary/30 transition-all hover:-translate-y-0.5 hover:shadow-2xl overflow-hidden"
       data-id="${r.id}">
      <div class="absolute top-0 left-0 bottom-0 w-1" style="background: ${accentBg};"></div>

      <div class="p-4 pl-5 space-y-3">
        <header class="flex items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1.5 mb-1">
              ${renderLensIcons(r)}
            </div>
            <div class="text-base font-semibold leading-tight truncate">${escapeHtml(r.river)}</div>
            <div class="text-xs text-base-content/55 truncate">${escapeHtml(r.section)}</div>
          </div>
          <span class="badge badge-ghost badge-sm shrink-0">${r.state}</span>
        </header>

        <div class="flex items-end gap-4">
          <div class="leading-none">
            <div class="font-display text-2xl text-primary tabular-nums" data-count="${r.current.cfs ?? 0}">
              ${formatCfs(r.current.cfs)}
            </div>
            <div class="text-[10px] uppercase tracking-wider text-base-content/50 mt-1">cfs</div>
          </div>

          <div id="${sparkId}" class="spark flex-1 ml-auto" aria-hidden="true"></div>
        </div>

        <div class="flex flex-wrap items-center gap-1.5 text-xs">
          ${renderStatusPills(r)}
          <span class="badge badge-sm badge-outline ${percentileBadgeClass(r.percentile.label)}">${formatPercent(r.percent_of_median)}</span>
          <span class="text-base-content/55" title="${r.trend_24h.direction}">${trendIcon(r.trend_24h.direction)} ${r.trend_24h.direction}</span>
          ${r.categories.includes('fly_fishing') && r.fish_species
            ? `<span class="ml-auto text-[11px] text-base-content/45 italic truncate">${escapeHtml(speciesShortList(r.fish_species))}</span>`
            : ''}
        </div>

        ${excerpt
          ? `<p class="text-xs text-base-content/65 leading-relaxed line-clamp-3">${escapeHtml(excerpt)}</p>`
          : `<p class="narrative-pending text-xs leading-relaxed">Generating insight…</p>`}
      </div>
    </a>
  `;
}

/**
 * Hero variant — taller, full narrative inline. Used for "Best Bets".
 */
export function renderRiverCardHero(r: RiverAnalysis, primaryCat: RiverCategory, rank: number): string {
  const primaryStatus = statusFor(r, primaryCat);
  const narrative = bestNarrative(r, primaryCat);
  const accentBg = accentColor(primaryStatus, primaryCat);
  const sparkId = `spark-${r.id}`;
  const eyebrow = `BEST BET #${rank}`;

  return `
    <a href="#/r/${r.id}"
       class="river-card group block relative rounded-2xl bg-base-200/70 backdrop-blur border border-primary/15 hover:border-primary/40 transition-all hover:-translate-y-1 hover:shadow-2xl overflow-hidden"
       data-id="${r.id}">
      <div class="absolute top-0 left-0 right-0 h-1" style="background: ${accentBg};"></div>
      <div class="p-5 space-y-3">
        <div class="flex items-center justify-between text-[10px] uppercase tracking-widest">
          <span class="text-primary font-display">${eyebrow}</span>
          <div class="flex items-center gap-2">
            ${renderLensIcons(r)}
            <span class="badge badge-ghost badge-sm">${r.state}</span>
          </div>
        </div>
        <header class="space-y-1">
          <div class="text-xl font-semibold leading-tight">${escapeHtml(r.river)}</div>
          <div class="text-sm text-base-content/60">${escapeHtml(r.section)}</div>
        </header>
        <div class="flex items-end gap-4">
          <div class="leading-none">
            <div class="font-display text-3xl text-primary tabular-nums">${formatCfs(r.current.cfs)}</div>
            <div class="text-[10px] uppercase tracking-wider text-base-content/50 mt-1">cfs</div>
          </div>
          <div id="${sparkId}" class="spark flex-1 ml-auto" aria-hidden="true"></div>
        </div>
        <div class="flex flex-wrap items-center gap-1.5 text-xs">
          ${renderStatusPills(r)}
          <span class="badge badge-sm badge-outline ${percentileBadgeClass(r.percentile.label)}">${formatPercent(r.percent_of_median)}</span>
          <span class="text-base-content/55">${trendIcon(r.trend_24h.direction)} ${r.trend_24h.direction}</span>
        </div>
        ${narrative
          ? `<p class="text-sm text-base-content/80 leading-relaxed">${escapeHtml(narrative)}</p>`
          : `<p class="narrative-pending text-sm leading-relaxed">Generating insight…</p>`}
      </div>
    </a>
  `;
}

/**
 * Compact single-line row — also shows both lens icons + multiple status
 * dots for dual-cat rivers.
 */
export function renderRiverRow(r: RiverAnalysis, primaryCat: RiverCategory): string {
  const status = statusFor(r, primaryCat);
  const dot = accentColor(status, primaryCat);
  const trend = r.trend_24h.direction;
  return `
    <a href="#/r/${r.id}" class="river-card river-row" data-id="${r.id}">
      <span class="row-dot" style="background:${dot}"></span>
      <span class="row-lenses">${renderLensIcons(r)}</span>
      <span class="row-name">${escapeHtml(r.river)}</span>
      <span class="row-section">${escapeHtml(r.section)}</span>
      <span class="row-state">${r.state}</span>
      <span class="row-cfs tabular-nums">${formatCfs(r.current.cfs)}</span>
      <span class="row-pct tabular-nums">${formatPercent(r.percent_of_median)}</span>
      <span class="row-trend" title="${trend}">${trendIcon(trend)}</span>
    </a>
  `;
}

/** Render small lens icons for all categories this river is in. */
function renderLensIcons(r: RiverAnalysis): string {
  return r.categories.map((c) => {
    const icon = c === 'fly_fishing' ? '🎣' : '🚣';
    const label = c === 'fly_fishing' ? 'Fly fishing' : 'Whitewater';
    return `<span class="lens-icon" title="${label}" aria-label="${label}">${icon}</span>`;
  }).join('');
}

/** Render one status pill per applicable lens — each labeled with its icon. */
function renderStatusPills(r: RiverAnalysis): string {
  return r.categories.map((c) => {
    const s = statusFor(r, c);
    const icon = c === 'fly_fishing' ? '🎣' : '🚣';
    return `<span class="badge badge-sm ${statusBadgeClass(s, c)} gap-1 whitespace-nowrap"><span aria-hidden="true">${icon}</span>${statusLabel(s, c)}</span>`;
  }).join('');
}

/** Prefer narrative for primaryCat; fall back to the other lens if empty. */
function bestNarrative(r: RiverAnalysis, primaryCat: RiverCategory): string | null {
  const primary = primaryCat === 'fly_fishing' ? r.narratives.fly_fishing : r.narratives.whitewater;
  if (primary) return primary;
  const other = primaryCat === 'fly_fishing' ? r.narratives.whitewater : r.narratives.fly_fishing;
  return other ?? null;
}

function accentColor(status: string, cat: RiverCategory): string {
  if (cat === 'fly_fishing') {
    return {
      ideal: 'oklch(72% 0.16 145)',
      high_but_fishable: 'oklch(72% 0.09 220)',
      too_low: 'oklch(76% 0.14 75)',
      too_high: 'oklch(63% 0.20 25)',
    }[status] ?? 'oklch(50% 0.02 250)';
  }
  return {
    runnable: 'oklch(72% 0.16 145)',
    high: 'oklch(72% 0.09 220)',
    very_high: 'oklch(76% 0.14 75)',
    too_low: 'oklch(76% 0.14 75)',
    flood: 'oklch(63% 0.20 25)',
  }[status] ?? 'oklch(50% 0.02 250)';
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  const cut = s.slice(0, n - 1);
  const space = cut.lastIndexOf(' ');
  return (space > n * 0.6 ? cut.slice(0, space) : cut) + '…';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
